import { isChatExportDownloadUrl } from '@heidi/evidence';
import type { CollectionResponse } from '@heidi/evidence/contracts';
import { listCollections, uploadUserDocument } from '@heidi/evidence/data';

export interface SaveExportAsArtefactParams {
  url: string;
  filename: string;
}

const generateClientUploadId = () =>
  typeof crypto?.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

const browserFetchUrl = (url: string): string => {
  if (!isChatExportDownloadUrl(url)) return url;

  const parsed = new URL(url, window.location.origin);
  parsed.searchParams.set('proxy', 'true');
  return parsed.toString();
};

const pickPersonalArtefactsCollection = (
  collections: CollectionResponse[]
): CollectionResponse | null => {
  const artefacts = collections.filter((collection) => collection.kind === 'artifacts');
  return artefacts.find((collection) => !collection.team_id) ?? null;
};

/**
 * Downloads a chat export URL and uploads it to Library as a Work artefact
 * (`document_kind: artifact`), routed into the user's personal artefacts
 * collection. Team is an explicit share, not the save default (EVI-2417).
 */
export async function saveExportAsArtefact({
  url,
  filename,
}: SaveExportAsArtefactParams): Promise<{ jobId: string }> {
  const response = await fetch(browserFetchUrl(url));
  if (!response.ok) {
    throw new Error(`Could not download export (${response.status})`);
  }

  const blob = await response.blob();
  const file = new File([blob], filename, {
    type: blob.type || 'application/octet-stream',
  });

  const { collections } = await listCollections();
  const artefactsCollection = pickPersonalArtefactsCollection(collections);

  const job = await uploadUserDocument(
    file,
    'user',
    undefined,
    artefactsCollection?.collection_id ?? null,
    generateClientUploadId(),
    { document_kind: 'artifact' }
  );

  return { jobId: job.job_id };
}
